import { useState } from "react";
import { bankAccountsParseMicrodepositAmount } from "../Helpers/bankAccountsParseMicrodepositAmount";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { T } from "@freightsimple/ui-core/Typography/T";

interface VerifyMicrodepositsDialogProps {
  bankName: string;
  lastFourDigits: string;
  onConfirm: (
    firstAmountCents: number,
    secondAmountCents: number,
  ) => Promise<void>;
  onCancel: () => void;
}

export const BankAccountsVerifyMicrodepositsDialog: React.FC<
  VerifyMicrodepositsDialogProps
> = (props) => {
  const [firstAmount, setFirstAmount] = useState("");
  const [secondAmount, setSecondAmount] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const firstCents = bankAccountsParseMicrodepositAmount(firstAmount);
  const secondCents = bankAccountsParseMicrodepositAmount(secondAmount);
  const canSubmit =
    firstCents !== undefined && secondCents !== undefined && !loading;

  async function handleConfirm() {
    if (firstCents === undefined || secondCents === undefined) return;
    setLoading(true);
    setError(undefined);
    try {
      await props.onConfirm(firstCents, secondCents);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack gap={4}>
      <T size="base" color="secondary">
        To finish setting up <strong>{props.bankName}</strong> ending in{" "}
        <strong>{props.lastFourDigits}</strong>, enter the two micro-deposit
        amounts Stripe sent to your bank account. They will appear in CAD, each
        less than $1.
      </T>

      <Row gap={6} align="start">
        <FormItem label="First amount">
          <TextInput
            type="text"
            placeholder="0.00"
            value={firstAmount}
            onChange={(e) => setFirstAmount(e.target.value)}
            startAdornment="$"
            width="md"
            data-testid="microdeposit-first-amount"
          />
        </FormItem>
        <FormItem label="Second amount">
          <TextInput
            type="text"
            placeholder="0.00"
            value={secondAmount}
            onChange={(e) => setSecondAmount(e.target.value)}
            startAdornment="$"
            width="md"
            data-testid="microdeposit-second-amount"
          />
        </FormItem>
      </Row>

      {error && (
        <T size="sm" color="red" testId="microdeposit-error">
          {error}
        </T>
      )}

      <Row gap={4} justify="end">
        <Button size="link" inheritFont onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          data-testid="microdeposit-verify-button"
          size="large"
          loading={loading}
          disabled={!canSubmit}
          disabledMessage="Enter both deposit amounts first"
          onClick={handleConfirm}
        >
          Verify
        </Button>
      </Row>
    </Stack>
  );
};
